import { useMemo, useState } from "react";
import { LayoutGrid, List, Users } from "lucide-react";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { SearchInput } from "@/components/ui/search-input";
import { FilterToolbar } from "@/components/ui/filter-toolbar";
import { EmptyState, ErrorState, LoadingState } from "@/components/ui/states";
import { UsersViewMode, useUsersViewMode } from "@/hooks/use-users-view-mode";
import { cn } from "@/lib/utils";
import type { UserProfile, UserWithProfile } from "@/types/user-profile";
import { CreateUserModal } from "./CreateUserModal";
import { UserProfileCard } from "./UserProfileCard";
import { UsersTable } from "./UsersTable";
import { UserKind, profileKindOf } from "./user-kind";

const ALL = "all";
const WITHOUT_PROFILE = "none";

const VIEW_MODES = [
  { mode: UsersViewMode.List, icon: List, label: "Lista" },
  { mode: UsersViewMode.Cards, icon: LayoutGrid, label: "Cards" },
];

interface Agencia {
  id: string;
  nome: string;
}

interface UsersPanelProps {
  kind: UserKind;
  users: UserWithProfile[];
  profiles: UserProfile[];
  agencias: Agencia[];
  isLoading: boolean;
  isError: boolean;
  onRetry: () => void;
  onChangeProfile: (user: UserWithProfile) => void;
  onUserCreated: () => void;
}

function matchesSearch(user: UserWithProfile, search: string) {
  if (!search) return true;
  const term = search.toLowerCase();
  return [user.full_name, user.username, user.email].some((value) => value?.toLowerCase().includes(term));
}

export function UsersPanel({
  kind,
  users,
  profiles,
  agencias,
  isLoading,
  isError,
  onRetry,
  onChangeProfile,
  onUserCreated,
}: UsersPanelProps) {
  const { mode, setMode } = useUsersViewMode();
  const [search, setSearch] = useState("");
  const [profileFilter, setProfileFilter] = useState<string>(ALL);
  const [agenciaFilter, setAgenciaFilter] = useState<string>(ALL);
  const isAgencyTab = kind === UserKind.Agency;

  const profilesOfKind = useMemo(
    () => profiles.filter((profile) => profileKindOf(profile) === kind),
    [profiles, kind],
  );

  const filteredUsers = users.filter((user) => {
    if (!matchesSearch(user, search)) return false;
    if (profileFilter === WITHOUT_PROFILE && user.access_profile_id !== null) return false;
    if (profileFilter !== ALL && profileFilter !== WITHOUT_PROFILE && String(user.access_profile_id) !== profileFilter) {
      return false;
    }
    if (isAgencyTab && agenciaFilter !== ALL && user.agencia_id !== agenciaFilter) return false;
    return true;
  });

  const hasFilters = Boolean(search) || profileFilter !== ALL || agenciaFilter !== ALL;

  return (
    <div className="space-y-6">
      <FilterToolbar>
        <SearchInput value={search} onChange={setSearch} placeholder="Buscar por nome ou e-mail..." />
        {isAgencyTab && (
          <Select value={agenciaFilter} onValueChange={setAgenciaFilter}>
            <SelectTrigger className="w-full sm:w-[200px]" aria-label="Filtrar por agência">
              <SelectValue placeholder="Agência" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value={ALL}>Todas as agências</SelectItem>
              {agencias.map((agencia) => (
                <SelectItem key={agencia.id} value={agencia.id}>
                  {agencia.nome}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        )}
        <Select value={profileFilter} onValueChange={setProfileFilter}>
          <SelectTrigger className="w-full sm:w-[200px]" aria-label="Filtrar por perfil">
            <SelectValue placeholder="Perfil" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value={ALL}>Todos os perfis</SelectItem>
            <SelectItem value={WITHOUT_PROFILE}>Sem perfil</SelectItem>
            {profilesOfKind.map((profile) => (
              <SelectItem key={profile.id} value={String(profile.id)}>
                {profile.name}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
        <span className="text-sm text-muted-foreground">
          {filteredUsers.length} {filteredUsers.length === 1 ? "usuário" : "usuários"}
        </span>
        <div className="flex max-w-full flex-wrap items-center gap-2 sm:ml-auto">
          <div className="flex overflow-hidden rounded-md border" role="group" aria-label="Modo de visualização">
            {VIEW_MODES.map(({ mode: optionMode, icon: Icon, label }) => (
              <button
                key={optionMode}
                type="button"
                onClick={() => setMode(optionMode)}
                className={cn(
                  "px-2.5 py-1.5",
                  mode === optionMode ? "bg-primary text-primary-foreground" : "bg-background hover:bg-muted",
                )}
                aria-label={label}
                aria-pressed={mode === optionMode}
                title={label}
              >
                <Icon className="h-4 w-4" />
              </button>
            ))}
          </div>
          <CreateUserModal profiles={profiles} onSuccess={onUserCreated} />
        </div>
      </FilterToolbar>

      {isLoading ? (
        <LoadingState variant={mode === UsersViewMode.List ? "list" : "grid"} />
      ) : isError ? (
        <ErrorState onRetry={onRetry} />
      ) : filteredUsers.length === 0 ? (
        <EmptyState
          icon={Users}
          title="Nenhum usuário encontrado"
          description={hasFilters ? "Ajuste a busca ou os filtros." : "Nenhum usuário cadastrado ainda."}
        />
      ) : mode === UsersViewMode.List ? (
        <UsersTable users={filteredUsers} kind={kind} onChangeProfile={onChangeProfile} />
      ) : (
        <div className="grid gap-6 md:grid-cols-2 lg:grid-cols-3">
          {filteredUsers.map((user) => (
            <UserProfileCard key={user.id} user={user} onChangeProfile={onChangeProfile} />
          ))}
        </div>
      )}
    </div>
  );
}
